// import global variables.
import {FOCUS_ELEMENTS, mql, resizeEvent, randomString} from '../utils/global';


export class Carousel {
    /**
     * カルーセル
     *
     * @constructor
     * @param {HTMLElement} root - ルートとなる要素
     * @param {Object} options - 設定の変更をする際のオブジェクト
     */
    constructor(root, options) {
        const config = {
            startIndex: 0,
            slidesToShow: 1,
            roop: false,
            indicatorType: 'dot',
            className: {
                carousel: {
                    list: 'p-carousel__list',
                    item: 'p-carousel__item',
                    animate: 'is-animate',
                    swipe: 'is-swipe',
                    reverse: 'is-reverse',
                    current: 'is-current',
                    next: 'is-next',
                    previous: 'is-previous'
                },
                controller: {
                    wrap: 'p-carousel-controller',
                    list: 'p-carousel-controller__list',
                    item: 'p-carousel-controller__item',
                    button: 'p-carousel-controller__button',
                    previous: 'p-carousel-controller__button--previous',
                    next: 'p-carousel-controller__button--next',
                    altText: 'u-altText'
                },
                indicator: {
                    wrap: 'p-carousel-indicator',
                    list: 'p-carousel-indicator__list',
                    item: 'p-carousel-indicator__item',
                    button: 'p-carousel-indicator__button',
                    altText: 'p-carousel-indicator__buttonText'
                },
                region: {
                    wrap: 'p-carousel-region',
                    text: 'p-carousel-region__text'
                }
            },
            text: {
                previous: '前へ',
                next: '次へ'
            },
            prefix: {
                id: 'carousel-'
            }
        };

        // ルートとなる要素が無い場合は実装しない
        if (!root) {
            return;
        }

        /**
         * config {Object} デフォルトの設定に個別の設定がマージされた状態の設定が格納されたオブジェクト
         * root {HTMLElement} モジュールのルートとなる要素
         * onlyScreen {Boolean} PCまたはSP画面のみでカルーセルを実装するか否か（data-only-screen属性で設定）
         * keepHeight {Boolean} 各アイテムの高さを統一するか否か（data-keep-height属性で設定）
         * slidesToShow {Number} 一度に表示するアイテムの数（data-wide-slide|data-narrow-slide属性が指定されている場合は属性値が適応される）
         * carouselList {HTMLElement} アイテムをラップする要素
         * carouselItem {NodeList} カルーセル表示する各アイテム
         * carouselLength {Number} カルーセルのアイテム数
         */
        this.config = Object.assign(config, options);
        this.root = root;
        this.onlyScreen = this.root.dataset.onlyScreen;
        this.keepHeight = this.root.dataset.keepHeight;
        this.slidesToShow = parseInt(this.root.dataset[mql.state === 'PC' ? 'wideSlide' : 'narrowSlide'], 10) || this.config.slidesToShow;
        this.carouselList = this.root.querySelector(`.${this.config.className.carousel.list}`);
        this.carouselItem = this.carouselList.querySelectorAll(`.${this.config.className.carousel.item}`);
        this.carouselLength = this.carouselItem.length;

        /**
         * split {Boolean} PC/SPでマークアップの変更（分割）が必要か否か（data-wide-split|data-narrow-split属性で指定）
         * splitData {Object} 分割が有効な場合、実装前の情報が保存される配列
         */
        this.split = this.root.dataset.hasOwnProperty('wideSplit') || this.root.dataset.hasOwnProperty('narrowSplit');
        this.splitData = this.split ? {
            PC: parseInt(this.root.dataset.wideSplit, 10) || 0,
            SP: parseInt(this.root.dataset.narrowSplit, 10) || 0,
            originList: this.carouselList,
            originItems: this.carouselItem,
            originLength: this.carouselLength
        } : {};

        // 分割する設定が有効な場合、マークアップを変更
        if (this.split) {
            this.splitCarouselItems();

        // 分割でない かつ アイテム数が一度に表示するアイテムの数と同じか下回る場合、カルーセルとして実装しない
        } else if (this.carouselLength <= this.slidesToShow) {
            return;
        }

        /**
         * state {Object} 状態管理用の変数を格納するオブジェクト
         * state.isUnset {Boolean} カルーセルの実装が解除されているか否か
         * state.isAnimate {Boolean} アニメーション中か否か
         * state.isDuplicate {Boolean} 次または前に表示する要素が重複しているか否か
         * state.isFirst {Boolean} カレント表示している要素が先頭か否か
         * state.isLast {Boolean} カレント表示している要素が末尾か否か
         * state.isClickable {Boolean} カルーセルアイテムのクリックを許可しているか否か
         * state.isDrag {Boolean} ドラッグされているか否か
         * state.isSwipe {Boolean} スワイプがされているか否か
         * state.swipeStart {Number} スワイプを開始した地点の位置情報
         * state.swipeEnd {Number} スワイプを終了した地点の位置情報
         * state.swipeDiff {Number} スワイプの開始地点と終了地点の差分
         */
        this.state = {
            isUnset: false,
            isAnimate: false,
            isDuplicate: false,
            isFirst: false,
            isLast: false,
            isClickable: true,
            isDrag: false,
            isSwipe: false,
            swipeStart: 0,
            swipeEnd: 0,
            swipeDiff: 0
        };

        /**
         * index {Object} index情報が格納されるオブジェクト
         * {show: Number[], past: Number, current: Number, previous: Number, next: Number}
         */
        this.index = this.updateIndex(this.config.startIndex);

        /**
         * controllPrevious {HTMLElement} 「前へ」ボタンの要素
         * controllNext {HTMLElement} 「次へ」ボタンの要素
         * controller {HTMLElement} 「前へ」「次へ」ボタンを含んだリスト
         * indicatorButton {HTMLElement[]} インジケーターのボタン要素
         * indicator {HTMLElement} インジケーターボタンを含んだリスト
         * regionNumber {HTMLElement} リージョンテキストの数字部分
         * region {HTMLElement} リージョンテキストの要素
         * focusableElements {NodeList[]} 各アイテム内存在するフォーカス可能な要素の解列
         * noFocusElements {HTMLElement[]} フォーカスを無効にする要素
         */
        this.controllPrevious = this.createControllButton('previous');
        this.controllNext = this.createControllButton('next');
        this.controller = this.createController();
        this.indicatorButton = [];
        this.indicator = this.createIndicator();
        this.regionNumber = null;
        this.region = this.createRegion();
        this.focusableElements = this.getFocusableElements();
        this.noFocusElements = [...this.focusableElements[this.index.previous], ...this.focusableElements[this.index.next]];

        /**
         * carouselWidth {Number} アイテム1つ当たりの横幅（アイテムの中の最大値）
         * carouselHeight {Number} アイテム1つ当たりの高さ（アイテムの中の最大値）
         * threshold {Number} スワイプの際、「前へ」または「次へ」切り替える基準値（超えていれば切り替える）
         */
        this.carouselWidth = 0;
        this.carouselHeight = 0;
        this.threshold = this.carouselWidth / 2;

        // 画面の表示するアイテム数がより全体の数が少ない場合はカルーセルとして実装しない
        if (!this.onlyScreen && !this.split && this.slidesToShow >= this.carouselLength) {
            return;
        }

        // 初期化
        this.init();
        this.addEvent();
    }

    /**
     * 初期化
     *
     * @return {Void}
     */
    init() {
        // SP / PCのみカルーセルにする場合、スクリーンサイズと設定が一致していなければリセット
        if (this.onlyScreen && mql.state !== this.onlyScreen) {
            this.reset();

            return;
        }

        // 属性付与
        this.carouselItem.forEach((item, idx) => {
            item.id = randomString(this.config.prefix.id);
            item.hidden = true;

            // コントロールボタンとの紐づけ
            if (this.indicatorButton.length) {
                this.indicatorButton[idx].setAttribute('aria-controls', item.id);
            }

            // 表示するindex番号と要素のindex番号が一致した場合は表示
            if (this.index.show.includes(idx)) {
                item.hidden = false;
                item.classList.add(this.config.className.carousel.current);
                item.style.order = this.index.show.indexOf(idx);
            }
        });

        // スワイプ対応用に表示している要素はフォーカス不可にする
        for (const element of this.noFocusElements) {
            element.tabIndex = -1;
        }

        // インジケーターを更新
        if (this.indicatorButton.length) {
            this.indicatorButton[this.index.past].classList.remove(this.config.className.carousel.current);
            this.indicatorButton[this.index.current].classList.add(this.config.className.carousel.current);
        }

        // ボタン・前後の要素の状態を更新
        this.updateCorrectState();
    }

    /**
     * マークアップの変更（分割）を行う
     *
     * @return {Viod}
     */
    splitCarouselItems() {
        const wrap = document.createElement('div');
        const items = [];
        const newItems = [];
        const splitLength = this.splitData[mql.state];
        let count = 0;

        // 分割の指定がない・または 0 の場合、元のマークアップを適用
        if (splitLength === 0) {
            this.root.removeChild(this.carouselList);
            this.root.appendChild(this.splitData.originList);

            this.carouselList = this.splitData.originList;
            this.carouselItem = this.splitData.originItems;
            this.carouselLength = this.splitData.originLength;
        }

        items[count] = [];
        wrap.classList.add(this.config.className.carousel.list);

        this.splitData.originItems.forEach((item, idx) => {
            // wrapに必要な子要素数を入れたら、countを1更新
            if (idx !== 0 && (idx % splitLength) === 0) {
                count++;
                items[count] = [];
            }

            items[count].push(item);
        });

        // 生成した配列から、マークアップを生成
        for (const item of items) {
            const parent = this.splitData.originList.cloneNode();

            // 配列内の各要素を修正・追加
            for (const element of item) {
                parent.classList.remove(this.config.className.carousel.list);
                parent.classList.add(this.config.className.carousel.item);
                parent.appendChild(element);
            }

            newItems.push(parent);
            wrap.appendChild(parent);
        }

        // 生成したマークアップをアペンド
        this.root.appendChild(wrap);
        this.root.removeChild(this.carouselList);

        // 各変数を構築したものに変更
        this.carouselList = wrap;
        this.carouselItem = newItems;
        this.carouselLength = newItems.length;
    }

    /**
     * カルーセル内に存在するフォーカス可能な要素を取得し配列にして返却
     *
     * @return {NodeList[]}
     */
    getFocusableElements() {
        const focusableElements = [];

        for (const item of this.carouselItem) {
            focusableElements.push(item.querySelectorAll(FOCUS_ELEMENTS));
        }

        return focusableElements;
    }

    /**
     * カルーセルアイテムの最大幅を返却
     *
     * @return {Number}
     */
    getWidth() {
        let width = 0;

        for (const item of this.carouselItem) {
            const itemWidth = item.getBoundingClientRect().width;

            if (itemWidth > width) {
                width = itemWidth;
            }
        }

        return width;
    }

    /**
     * カルーセルアイテムの最大高さを返却
     *
     * @return {Number}
     */
    getHeight() {
        let height = 0;

        // 高さ保存のオプションが有効でない場合 0 を返却
        if (!this.keepHeight) {
            return 0;
        }

        for (const item of this.carouselItem) {
            const itemHeight = item.offsetHeight;

            if (itemHeight > height) {
                height = itemHeight;
            }
        }

        return height;
    }

    /**
     * index情報を更新し返却します
     *
     * @param {Number} idx - 現在表示している要素のindex番号（currentIndex）
     * @return {Object} {show: Number[], past: Number, current: Number, previous: Number, next: Number}
     */
    updateIndex(idx) {
        const indexes = {
            show: [],
            past: typeof this.index === 'undefined' ? 0 : this.index.current,
            current: idx,
            previous: 0,
            next: 0
        };

        // 表示する数の分index番号を取得
        for (let i = 0; i < this.slidesToShow; i++) {
            let index = i + idx;

            // 存在しないindex番号の場合
            if (index >= this.carouselLength) {
                index -= this.carouselLength;
            }

            indexes.show.push(index);
        }

        indexes.previous = (indexes.show[0] - 1) < 0 ? this.carouselLength - 1 : indexes.show[0] - 1;
        indexes.next = (indexes.show.slice(-1)[0] + 1) === this.carouselLength ? 0 : indexes.show.slice(-1)[0] + 1;

        // 重複フラグを更新
        if (indexes.previous === indexes.next) {
            this.state.isDuplicate = true;
        }

        // 非ループの場合、先頭・末尾フラグを更新
        if (!this.config.roop) {
            this.state.isFirst = indexes.previous === this.carouselLength - 1;
            this.state.isLast = indexes.next === 0;
        }

        return indexes;
    }

    /**
     * index情報を元に操作ボタン・前後の要素・ステートメントテキストを更新
     *
     * @return {Void}
     */
    updateCorrectState() {
        // パネルが一枚しかない場合は更新しない
        if (this.state.isFirst && this.state.isLast) {
            return;
        }

        // 非ループ かつ 先頭フラグがtrueの場合、前へボタンをdisabled、次に表示する要素のみ表示
        if (!this.config.roop && this.state.isFirst) {
            this.carouselItem[this.index.next].classList.add(this.config.className.carousel.next);

        // 非ループ かつ 末尾フラグがtrueの場合、次へボタンをdisabled、前に表示する要素のみ表示
        } else if (!this.config.roop && this.state.isLast) {
            this.carouselItem[this.index.previous].classList.add(this.config.className.carousel.previous);

        // ループの場合、または 非ループだが先頭・末尾のフラグがどちらもtrueでない場合
        } else {
            this.carouselItem[this.index.next].classList.add(this.config.className.carousel.next);
            this.carouselItem[this.index.previous].classList.add(this.config.className.carousel.previous);
        }

        // ボタンの状態を更新
        this.controllPrevious.disabled = !this.config.roop && this.state.isFirst;
        this.controllNext.disabled = !this.config.roop && this.state.isLast;

        // ステートメントテキストを更新
        this.regionNumber.textContent = this.index.show.map((idx) => idx + 1).join('、');
    }

    /**
     * インジケーターを生成
     *
     * @return {HTMLElement}
     */
    createIndicator() {
        const fragment = document.createDocumentFragment();
        const wrap = document.createElement('div');
        const list = document.createElement('ul');

        if (this.root.dataset.indicator === 'false') {
            return null;
        }

        this.displayElem([wrap]);

        wrap.classList.add(this.config.className.indicator.wrap);
        list.classList.add(this.config.className.indicator.list);

        for (let i = 0; i < this.carouselLength; i++) {
            const item = document.createElement('li');
            const button = document.createElement('button');
            const altText = document.createElement('span');

            item.classList.add(this.config.className.indicator.item);
            button.type = 'button';
            button.classList.add(this.config.className.indicator.button);
            button.dataset.index = i;
            altText.classList.add(this.config.className.indicator.altText);
            altText.textContent = i + 1;

            if (this.config.indicatorType === 'image') {
                const img = this.carouselItem[i].getElementsByTagName('img');

                button.style.backgroundImage = `url(${img[0].src})`;
            }

            // カレントクラスを付与
            if (this.index.current === i) {
                button.classList.add(this.config.className.carousel.current);
            }

            button.appendChild(altText);
            this.indicatorButton.push(button);

            item.appendChild(button);
            fragment.appendChild(item);
        }

        list.appendChild(fragment);
        wrap.appendChild(list);
        this.root.appendChild(wrap);

        return wrap;
    }

    /**
     * コントロールボタン（前へ・次へボタンのリスト）を生成
     *
     * @return {HTMLElement | null}
     */
    createController() {
        const wrap = document.createElement('div');
        const list = document.createElement('ul');

        // コントロールボタンの生成を無効にするオプションが有効の場合
        if (this.root.dataset.controller === 'false') {
            return null;
        }

        this.displayElem([wrap]);

        wrap.classList.add(this.config.className.controller.wrap);
        list.classList.add(this.config.className.controller.list);

        for (const button of [this.controllPrevious, this.controllNext]) {
            const item = document.createElement('li');

            item.classList.add(this.config.className.controller.item);

            item.appendChild(button);
            list.appendChild(item);
        }

        wrap.appendChild(list);
        this.root.appendChild(wrap);

        return wrap;
    }

    /**
     * コントロールボタン（前へ・次へボタン自体）を生成
     *
     * @param {String} direction - ボタンの示す方向（previous|next）
     * @return {HTMLElement}
     */
    createControllButton(direction) {
        const button = document.createElement('button');
        const altText = document.createElement('span');

        button.type = 'button';
        button.classList.add(this.config.className.controller.button);
        button.classList.add(this.config.className.controller[direction]);
        altText.classList.add(this.config.className.controller.altText);
        altText.textContent = this.config.text[direction];

        button.appendChild(altText);

        return button;
    }

    /**
     * ライブリージョン用の要素を生成
     *
     * @return {HTMLElement}
     */
    createRegion() {
        const wrap = document.createElement('div');
        const text = document.createElement('p');
        const num = document.createElement('span');

        wrap.classList.add(this.config.className.region.wrap);
        text.classList.add(this.config.className.region.text);
        text.setAttribute('aria-live', 'polite');
        text.setAttribute('aria-atomic', 'true');

        num.textContent = this.index.show.map((idx) => idx + 1).join('、');
        text.innerHTML = `${this.carouselLength}件中${num.outerHTML}件目を表示`;

        wrap.appendChild(text);
        this.root.appendChild(wrap);

        this.regionNumber = text.querySelector('span');

        return wrap;
    }

    /**
     * イベントリスナーを登録
     *
     * @return {Viod}
     */
    addEvent() {
        // インジケーターのイベント
        for (const indicator of this.indicatorButton) {
            indicator.addEventListener('click', (e) => {
                const targetIndex = parseInt(e.currentTarget.dataset.index, 10);

                if (targetIndex === this.index.current) {
                    return;
                }

                this.slide(targetIndex);
            });
        }

        // 前へボタンのイベント
        this.controllPrevious.addEventListener('click', () => {
            this.slide(this.index.current - 1);
        });

        // 次へボタンのイベント
        this.controllNext.addEventListener('click', () => {
            this.slide(this.index.current + 1);
        });


        // スライド後のイベント
        this.carouselList.addEventListener('transitionend', (e) => {
            // transform以外のトランジションは検知しない
            if (e.propertyName !== 'transform') {
                return;
            }
            this.transitionAfter();
        });

        // スワイプ用イベント
        for (const event of ['touchstart', 'mousedown']) {
            this.carouselList.addEventListener(event, this.swipeStart.bind(this), {passive: true});
        }

        for (const event of ['touchmove', 'mousemove']) {
            this.carouselList.addEventListener(event, this.swipeMove.bind(this), {passive: true});
        }

        for (const event of ['touchend', 'mouseup', 'touchcancel', 'mouseleave']) {
            this.carouselList.addEventListener(event, this.swipeEnd.bind(this), {passive: true});
        }

        this.carouselList.addEventListener('click', (e) => {
            if (!this.state.isClickable || this.state.isAnimate) {
                e.stopImmediatePropagation();
                e.stopPropagation();
                e.preventDefault();
            }
        }, true);

        for (const item of this.carouselItem) {
            item.addEventListener('dragstart', (e) => {
                e.preventDefault();
            });
        }

        // レスポンシブベント
        window.addEventListener(mql.event, this.screenChange.bind(this));

        // リサイズ後、横幅を再取得
        window.addEventListener(resizeEvent.end, () => {
            this.carouselWidth = this.getWidth();
            this.threshold = this.carouselWidth / 2;

            // 高さを保存するオプションが有効な場合、高さを取得・更新
            if (this.keepHeight) {
                this.carouselHeight = this.getHeight();

                for (const item of this.carouselItem) {
                    item.style.minHeight = `${this.carouselHeight}px`;
                }
            }
        });
    }

    /**
     * スライドを実行
     *
     * @param {Number} targetIndex - カレントとするindex番号
     * @return {Viod}
     */
    slide(targetIndex) {
        const indexDiff = this.index.current - targetIndex;
        const isNegative = indexDiff < 0;
        let currentIndex = targetIndex;
        let duration = '';

        // アニメーション中は無効 または ループでない かつ インデックスが不正な場合、何もしない
        if (this.state.isAnimate ||
            (!this.config.roop && (currentIndex < 0 || currentIndex >= this.carouselLength))
        ) {
            return;
        }

        if (this.carouselWidth === 0) {
            this.carouselWidth = this.getWidth();
            this.threshold = this.carouselWidth / 2;
        }

        if (this.keepHeight && this.carouselHeight === 0) {
            this.carouselHeight = this.getHeight();

            for (const item of this.carouselItem) {
                item.style.minHeight = `${this.carouselHeight}px`;
            }
        }

        // 不正なインデックス番号を解消
        if (currentIndex < 0) {
            currentIndex = this.carouselLength - 1;
        } else if (currentIndex >= this.carouselLength) {
            currentIndex = 0;
        }

        // アニメーションを有効化
        this.state.isAnimate = true;
        this.carouselList.classList.add(this.config.className.carousel.animate);
        duration = window.getComputedStyle(this.carouselList).getPropertyValue('transition-duration');

        // 負の方向←へのスライドの場合、reverseクラスを付与
        if (!isNegative) {
            this.carouselList.classList.add(this.config.className.carousel.reverse);
        }

        // 次 と 前 のアイテムが重複する場合、進行方向に合わせ、不要なクラスを削除
        if (this.state.isDuplicate) {
            this.carouselItem[this.index.next].classList.remove(isNegative ? this.config.className.carousel.previous : this.config.className.carousel.next);
        }

        // index情報を更新
        this.index = this.updateIndex(currentIndex);

        // 整列してスライド
        this.alignment(isNegative, 1 < Math.abs(indexDiff));
        this.carouselList.style.transform = `translateX(${this.carouselWidth * (indexDiff)}px)`;

        // transitionの設定がない場合
        if (duration === '' || parseFloat(duration) === 0) {
            this.transitionAfter();
        } else if (this.state.isAnimate) {
            this.state.isAnimate = false;
        }

        // インジケーターを更新
        if (this.indicatorButton.length) {
            this.indicatorButton[this.index.past].classList.remove(this.config.className.carousel.current);
            this.indicatorButton[this.index.current].classList.add(this.config.className.carousel.current);
        }
    }

    /**
     * カルーセルアイテムを整列
     *
     * @param {Boolean} isNegative - スライドさせる方向が正の方向→か否か
     * @param {Boolean} isMultiple - 1個以上のスライドを発生させるか否か
     * @return {Viod}
     */
    alignment(isNegative, isMultiple) {
        const aleadyOrderProperty = [];

        this.carouselItem.forEach((item, idx) => {
            let itemOrder = item.style.order;

            // 正の方向←かつpreviousクラスを持っている場合 または 負の方向→かつnextクラスを持っている場合、transformで位置の調整をする
            if (
                (isNegative && item.classList.contains(this.config.className.carousel.previous)) ||
                (!isNegative && item.classList.contains(this.config.className.carousel.next))
            ) {
                aleadyOrderProperty.push(item);
                item.style.transform = `translateX(${isNegative ? -(this.carouselWidth * (this.carouselLength)) : this.carouselWidth * (this.carouselLength)}px)`;
            }

            // すでにorderのstyleが指定されている場合
            if (itemOrder) {
                // スライドさせる方向が負の方向←であれば降順に並べるため負の数にする
                if (!isNegative) {
                    item.style.order = -(itemOrder);
                }

                // 1個以上のスライドが発生しない場合ここで抜け出す
                if (!isMultiple) {
                    return;
                }

                aleadyOrderProperty.push(item);
                item.dataset.order = itemOrder;

                return;
            }

            // スライドさせる方向が正の方向→であれば昇順に並べる
            if (isNegative) {
                itemOrder = idx > this.index.past ? idx - this.index.past : (idx - this.index.past) + this.carouselLength;

            // スライドさせる方向が負の方向←であれば降順に並べる
            } else {
                itemOrder = idx < this.index.past ? -(idx - this.index.past) : -((idx - this.index.past) - this.carouselLength);
            }

            item.style.order = itemOrder;
        });

        // すでにorderのstyleを持っていた要素は、不可視になってからスタイルの更新を行う
        for (const element of aleadyOrderProperty) {
            const observer = new IntersectionObserver((entries) => {
                if (!entries) {
                    return;
                }

                for (const entry of entries) {
                    const {target} = entry;
                    const {order} = target.dataset;

                    // 可視状態の場合何もしない
                    if (entry.isIntersecting) {
                        return;
                    }

                    // 不可視かつdata-order属性がない場合、transformをリセット
                    if (!order) {
                        target.style.transform = '';

                    // 不可視かつdata-order属性がある場合、data-order属性をorderのstyleとして登録しtransformで位置を調整する
                    } else {
                        target.removeAttribute('data-order');
                        target.style.transform = `translateX(${isNegative ? this.carouselWidth * (this.carouselLength) : -(this.carouselWidth * (this.carouselLength))}px)`;
                    }

                    // 監視を解除
                    observer.unobserve(target);
                }
            });

            // 監視を開始
            observer.observe(element);
        }
    }

    /**
     * スクロールのtransition後の処理
     *
     * @return {Viod}
     */
    transitionAfter() {
        // フォーカスさせない要素を更新
        this.noFocusElements = [...this.focusableElements[this.index.previous], ...this.focusableElements[this.index.next]];

        // rootのクラスを削除
        this.carouselList.classList.remove(this.config.className.carousel.animate);
        this.carouselList.classList.remove(this.config.className.carousel.swipe);
        this.carouselList.classList.remove(this.config.className.carousel.reverse);

        // アイテムの状態を更新
        this.carouselItem.forEach((item, idx) => {
            item.style.transform = '';
            item.classList.remove(this.config.className.carousel.current);
            item.classList.remove(this.config.className.carousel.previous);
            item.classList.remove(this.config.className.carousel.next);
            item.removeAttribute('data-order');
            item.hidden = true;
            item.style.order = '';

            // tabindex属性を削除
            for (const element of this.focusableElements[idx]) {
                element.removeAttribute('tabindex');
            }

            // 表示するindex番号に一致する場合
            if (this.index.show.includes(idx)) {
                item.hidden = false;
                item.classList.add(this.config.className.carousel.current);
                item.style.order = this.index.show.indexOf(idx);
            }
        });

        // フォーカスさせない要素にtabindex="-1"を付与
        for (const element of this.noFocusElements) {
            element.tabIndex = -1;
        }

        // transformを0の位置に設定
        this.carouselList.style.transform = 'translateX(0px)';

        // ボタン・前後の要素の状態を更新
        this.updateCorrectState();

        // フラグを更新
        this.state.isAnimate = false;
        this.state.isClickable = true;
    }

    /**
     * スワイプ（ドラッグ）の開始を検知した際の処理
     *
     * @param {TouchEvent|MouseEvent} event - イベントリスナーからのイベントオブジェクト
     * @return {Viod}
     */
    swipeStart(event) {
        const isTouch = typeof event.changedTouches !== 'undefined';
        const touchFingers = isTouch ? event.changedTouches.length : 1;

        this.state.isDrag = true;

        if (this.carouselWidth === 0) {
            this.carouselWidth = this.getWidth();
            this.threshold = this.carouselWidth / 2;
        }

        // アニメーション中 または 複数の指で操作する場合は動作させない
        if (this.state.isAnimate || touchFingers !== 1) {
            this.state.swipeStart = 0;
            this.state.swipeEnd = 0;
            this.state.swipeDiff = 0;

            return;
        }

        // スワイプがスタートしたときの位置情報を保存
        this.state.swipeStart = isTouch ? event.changedTouches[0].pageX : event.clientX;
        this.carouselList.classList.add(this.config.className.carousel.swipe);
    }

    /**
     * スワイプ（ドラッグ）中の処理
     *
     * @param {TouchEvent|MouseEvent} event - イベントリスナーからのイベントオブジェクト
     * @return {Viod}
     */
    swipeMove(event) {
        const isTouch = typeof event.changedTouches !== 'undefined';
        const touchFingers = isTouch ? event.changedTouches.length : 1;

        // dragフラグがfalseまたは複数の指で操作する場合は動作させない
        if (this.state.isAnimate || !this.state.isDrag || touchFingers !== 1) {
            return;
        }

        // スワイプの終了地点とその差を保存
        this.state.swipeEnd = isTouch ? event.changedTouches[0].pageX : event.clientX;
        this.state.swipeDiff = Math.round(this.state.swipeStart - this.state.swipeEnd);

        // 5px以上のスワイプを検出したらスワイプ中とみなす
        if (this.state.swipeDiff > 5) {
            this.state.isSwipe = true;
            // アイテムのクリックを無効にする
            this.state.isClickable = false;
        }

        // アイテムの横幅以上のスワイプの検知 または 非ループかつカレント表示の要素が一番初めか最後の要素の場合、20px以上スワイプさせない
        if (
            Math.abs(this.state.swipeDiff) > this.carouselWidth ||
            (this.state.isFirst && this.state.swipeDiff < -20) ||
            (this.state.isLast && this.state.swipeDiff > 20)
        ) {
            return;
        }

        // 発生した差分だけ移動させる
        this.carouselList.style.transform = `translateX(${-(this.state.swipeDiff)}px)`;
    }

    /**
     * スワイプ（ドラッグ）の終了を検知した際の処理
     *
     * @return {Viod}
     */
    swipeEnd() {
        // フラグを更新
        this.state.isDrag = false;
        this.state.isSwipe = false;

        // swipeDiffが0またはundefinedの時は何もしない
        if (!this.state.swipeDiff) {
            return;
        }

        // 一番初めが表示されている時の負の方向←スワイプ または 一番最後が表示されているときの正の方向→スワイプは0の地点へ戻す
        if (
            (this.state.isFirst && this.state.swipeDiff < 0) ||
            (this.state.isLast && this.state.swipeDiff > 0)
        ) {
            this.carouselList.classList.add(this.config.className.carousel.animate);
            this.carouselList.style.transform = 'translateX(0)';

            return;
        }

        // 正の方向→のスワイプかつ、スワイプ量が閾値を超えている場合
        if (this.state.swipeDiff > 0 && this.state.swipeDiff > this.threshold) {
            this.slide(this.index.current + 1);

        // 負の方向←のスワイプかつ、スワイプ量が閾値を超えている場合
        } else if (this.state.swipeDiff < 0 && -(this.state.swipeDiff) > this.threshold) {
            this.slide(this.index.current - 1);

        // 該当しない場合0の地点へ戻す
        } else {
            this.carouselList.classList.add(this.config.className.carousel.animate);
            this.carouselList.style.transform = 'translateX(0)';
        }

        // データをリセット
        this.state.swipeStart = 0;
        this.state.swipeEnd = 0;
        this.state.swipeDiff = 0;
    }

    /**
     * カルーセルアイテムが最大表示数よりも少ない場合、引数で渡された要素を非表示にする
     * 要素は配列で渡す
     *
     * @param {Array<element>} elems 非表示にする要素の配列
     */
    displayElem(elems) {
        for (const elem of elems) {
            if (this.carouselLength <= this.slidesToShow) {
                elem.classList.add('u-none');
            } else {
                elem.classList.remove('u-none');
            }
        }
    }

    /**
     * ブレークポイントをまたいだ際の処理
     *
     * @return {Viod}
     */
    screenChange() {
        // slidesToShow を更新
        this.slidesToShow = parseInt(this.root.dataset[mql.state === 'PC' ? 'wideSlide' : 'narrowSlide'], 10) || this.config.slidesToShow;

        // 分割のオプションが有効の場合、再構築を実行
        if (this.split) {
            this.reset();
            this.rebuild();

            return;
        }

        // this.slidesToShowの数に変更があったらカルーセルを更新
        if (this.index.show.length !== this.slidesToShow) {
            this.slide(this.index.current);
            this.transitionAfter();
        }

        // 以降はdata-only-screen属性がない場合は実行しない
        if (!this.onlyScreen) {
            return;
        }

        // スクリーン設定と現在のスクリーンサイズが一致していたらカルーセルを構築
        if (mql.state === this.onlyScreen) {
            this.index = this.updateIndex(this.config.startIndex);
            this.state.isUnset = false;

            this.root.appendChild(this.controller);
            this.root.appendChild(this.indicator);
            this.root.appendChild(this.region);

            this.init();

        // 一致しない場合リセット
        } else {
            this.reset();
        }
    }

    /**
     * リセット処理
     *
     * @return {Viod}
     */
    reset() {
        // 親要素のクラス・スタイルを削除
        this.carouselList.classList.remove(this.config.className.carousel.animate);
        this.carouselList.classList.remove(this.config.className.carousel.reverse);
        this.carouselList.style.transform = '';

        // アイテムのクラス・スタイルを削除
        this.carouselItem.forEach((item, idx) => {
            item.hidden = false;
            item.style.order = '';
            item.style.transform = '';
            item.removeAttribute('id');
            item.removeAttribute('data-order');
            item.classList.remove(this.config.className.carousel.current);
            item.classList.remove(this.config.className.carousel.previous);
            item.classList.remove(this.config.className.carousel.next);

            // tanindex属性を削除
            for (const element of this.focusableElements[idx]) {
                element.removeAttribute('tabindex');
            }
        });

        // 生成したパーツを削除
        if (this.root.dataset.controller !== 'false') {
            this.root.removeChild(this.controller);
        }

        if (this.root.dataset.indicator !== 'false') {
            this.root.removeChild(this.indicator);
        }

        this.root.removeChild(this.region);

        // アンセットフラグを更新
        this.state.isUnset = true;

        // フラグを初期値に更新
        this.state.isAnimate = false;
        this.state.isClickable = true;
        this.state.isDuplicate = false;
        this.state.isDrag = false;
        this.state.isSwipe = false;
    }

    /**
     * 再構築処理
     *
     * @return {Viod}
     */
    rebuild() {
        this.splitCarouselItems();
        window.removeEventListener(mql.event, this.screenChange.bind(this));

        this.state = {
            isDuplicate: false,
            isFirst: false,
            isLast: false
        };

        this.index = this.updateIndex(this.config.startIndex);
        this.indicatorButton = [];
        this.indicator = this.createIndicator();
        this.controller = this.createController();
        this.regionNumber = null;
        this.region = this.createRegion();
        this.focusableElements = this.getFocusableElements();
        this.noFocusElements = [...this.focusableElements[this.index.previous], ...this.focusableElements[this.index.next]];

        // 高さを保存するオプションが有効な場合、高さを取得・更新
        if (this.keepHeight) {
            this.carouselHeight = this.getHeight();

            for (const item of this.carouselItem) {
                item.style.minHeight = `${this.carouselHeight}px`;
            }
        }

        this.init();
        this.addEvent();
    }
}

/**
 * オプション デフォルト設定
 *
 * @property {Number} startIndex - カルーセル実装時先頭に表示する要素のIndex番号
 * @property {Number} slidesToShow - 一度に表示するスライド数
 * @property {Boolean} roop - 無限ループにするか否か
 * @property {Object} className.carousel - カルーセル本体に関連するクラス名
 * @property {Object} className.controller - 操作ボタンに関連するクラス名
 * @property {Object} className.indicator - インジケーターに関連するクラス名
 * @property {Object} className.region - ライブリージョンに関連するクラス名
 * @property {String} text.previous - 「前へ」ボタンに使用されるラベルテキスト
 * @property {String} text.next - 「前へ」ボタンに使用されるラベルテキスト
 * @property {String} prefix.id - id名の接頭辞
 */
export default () => {
    // 汎用カルーセル
    document.querySelectorAll('.js-carousel').forEach((element) => new Carousel(element));

    // TOP用カルーセル
    document.querySelectorAll('.js-carousel--top').forEach((element) => new Carousel(element, {
        roop: true,
        className: {
            carousel: {
                list: 'p-carousel-top__list',
                item: 'p-carousel-top__item',
                animate: 'is-animate',
                swipe: 'is-swipe',
                reverse: 'is-reverse',
                current: 'is-current',
                next: 'is-next',
                previous: 'is-previous'
            },
            controller: {
                wrap: 'p-carousel-top-controller',
                list: 'p-carousel-top-controller__list',
                item: 'p-carousel-top-controller__item',
                button: 'p-carousel-top-controller__button',
                previous: 'p-carousel-top-controller__button--previous',
                next: 'p-carousel-top-controller__button--next',
                altText: 'u-altText'
            },
            indicator: {
                wrap: 'p-carousel-top-indicator',
                list: 'p-carousel-top-indicator__list',
                item: 'p-carousel-top-indicator__item',
                button: 'p-carousel-top-indicator__button',
                altText: 'u-altText'
            },
            region: {
                wrap: 'p-carousel-region',
                text: 'p-carousel-region__text'
            }
        },
        prefix: {
            id: 'carousel-top-'
        }
    }));

    // 製品詳細
    document.querySelectorAll('.js-carousel--product').forEach((element) => new Carousel(element, {
        roop: true,
        indicatorType: 'image',
        className: {
            carousel: {
                list: 'p-carousel-product__list',
                item: 'p-carousel-product__item',
                animate: 'is-animate',
                swipe: 'is-swipe',
                reverse: 'is-reverse',
                current: 'is-current',
                next: 'is-next',
                previous: 'is-previous'
            },
            controller: {
                wrap: 'p-carousel-product-controller',
                list: 'p-carousel-product-controller__list',
                item: 'p-carousel-product-controller__item',
                button: 'p-carousel-product-controller__button',
                previous: 'p-carousel-product-controller__button--previous',
                next: 'p-carousel-product-controller__button--next',
                altText: 'u-altText'
            },
            indicator: {
                wrap: 'p-carousel-product-indicator',
                list: 'p-carousel-product-indicator__list',
                item: 'p-carousel-product-indicator__item',
                button: 'p-carousel-product-indicator__button',
                altText: 'u-altText'
            },
            region: {
                wrap: 'p-carousel-region',
                text: 'p-carousel-region__text'
            }
        },
        text: {
            previous: '前へ',
            next: '次へ'
        },
        prefix: {
            id: 'carousel-product-'
        }
    }));
};
